<template>
  <section class="hero-section">
    <div class="hero-media"  @click="handleBannerClick">
      <img
        :src="getMediaUrl(currentBanner.url) + '?v=' + currentBannerIndex"
        :alt="'Banner ' + currentBannerIndex"
        class="hero-image"
        @load="onImgLoad"
        @error="onImgError"
        v-show="!imageLoading"
      >
      <div v-if="imageLoading" class="banner-loading">
        <span class="spinner"></span>
      </div>
    </div>

    <!-- Banner文案 -->
    <div class="banner-content" @click="handleBannerClick">
      <h2 class="banner-text">{{ currentBanner.content }}</h2>
    </div>

    <!-- 轮播指示器 -->
    <div class="banner-indicators">
      <span
        v-for="(_, index) in totalBannerCount"
        :key="index"
        :class="['banner-line', { active: currentBannerIndex === index}]"
        @click="setActiveBanner(index)"
      ></span>
    </div>

    <!-- 轮播箭头 -->
    <div class="banner-arrows">
      <button class="banner-arrow banner-prev" @click="prevBanner">
        <span>&lt;</span>
      </button>
      <button class="banner-arrow banner-next" @click="nextBanner">
        <span>&gt;</span>
      </button>
    </div>
  </section>
</template>

<script>
export default {
  name: 'BannerSlider',
  data() {
    return {
      currentBannerIndex: 0,
      apiBanners: [], // 存储从API获取的banner数据
      bannerTimer: null,
      imageLoading: true, // 新增：图片加载状态
    };
  },
  computed: {
    // 计算当前banner
    currentBanner() {
      return this.apiBanners[this.currentBannerIndex] || {};
    },
    // 计算总banner数量
    totalBannerCount() {
      return this.apiBanners.length; 
    }
  },
  created() {
    // 获取banner数据
    this.fetchBannerList();
  },
  beforeDestroy() {
    // 清除定时器
    if (this.bannerTimer) {
      clearInterval(this.bannerTimer);
    }
  },
  methods: {
    // 获取banner列表数据
    fetchBannerList() {
      this.$api.home.getBannerList()
        .then(response => {
          console.log('Banner API 返回数据结构:', response);
          if (response.rows && response.rows.length > 0) {
            this.apiBanners = response.rows;
            console.log('从API获取的Banner数据:', this.apiBanners);
            // 获取数据后启动轮播
            this.startBannerRotation();
            this.imageLoading = true; // 新增：首次加载时显示loading
          }
        })
        .catch(error => {
          console.error('获取Banner列表失败:', error);
        });
    },

    // 获取媒体(图片)完整URL
    getMediaUrl(url) {
      return process.env.VUE_APP_IMAGE_URL + url;
    },

    // 处理banner点击事件
    handleBannerClick() {
      if (this.currentBanner.jumpUrl) {
        // 如果是外部链接
        if (this.currentBanner.jumpUrl.startsWith('http')) {
          window.open(this.currentBanner.jumpUrl, '_blank');
        } else {
          // 内部路由跳转
          this.$router.push(this.currentBanner.jumpUrl);
        }
      }
    },

    // 启动轮播
    startBannerRotation() {
      // 只有当有多个banner时才启动轮播
      if (this.totalBannerCount > 1) {
        this.bannerTimer = setInterval(() => {
          this.currentBannerIndex = (this.currentBannerIndex + 1) % this.totalBannerCount;
        }, 5000); // 5秒切换一次
      }
    },

    // 图片加载完成
    onImgLoad() {
      this.imageLoading = false;
    },
    // 图片加载失败
    onImgError(event) {
      this.imageLoading = false;
      event.target.src = 'https://via.placeholder.com/800x300?text=No+Image';
    },
    // 设置当前banner
    setActiveBanner(index) {
      this.currentBannerIndex = index;
      this.imageLoading = true; // 新增：切换时显示loading
      // 重置自动轮播定时器
      if (this.bannerTimer) {
        clearInterval(this.bannerTimer);
      }
      this.startBannerRotation();
    },

    // 上一个banner
    prevBanner() {
      this.currentBannerIndex = (this.currentBannerIndex - 1 + this.totalBannerCount) % this.totalBannerCount;
      this.imageLoading = true; // 新增：切换时显示loading
      // 重置自动轮播定时器
      if (this.bannerTimer) {
        clearInterval(this.bannerTimer);
      }
      this.startBannerRotation();
    },

    // 下一个banner
    nextBanner() {
      this.currentBannerIndex = (this.currentBannerIndex + 1) % this.totalBannerCount;
      this.imageLoading = true; // 新增：切换时显示loading
      // 重置自动轮播定时器
      if (this.bannerTimer) {
        clearInterval(this.bannerTimer);
      }
      this.startBannerRotation();
    }
  }
};
</script>

<style lang="scss" scoped>
// 变量
$primary-color: #ed1c24; // 红色
$light-text: #fff;
$mobile: 767px;
$tablet: 1024px;
$header-height-desktop: 86px; // 桌面端header高度
$header-height-mobile: 76px; // 移动端header高度

/* Hero Section */
.hero-section {
  position: relative;
  color: $light-text;
  overflow: hidden;
  width: 100%;
  height: 100vh; /* 填满整个视口高度 */
  min-height: 600px; /* 最小高度以确保在小屏幕上显示正常 */
  padding-top: $header-height-desktop; /* 为header预留空间 */
  box-sizing: border-box; /* 确保padding不会增加总高度 */

  @media (min-width: 1801px) and (max-width: 1920px) {
    /* 针对1920宽的屏幕优化 */
    width: 100vw;
    height: 100vh;
  }

  @media (max-width: 1800px) {
    /* 针对1800宽的屏幕优化 */
    width: 100vw;
    height: 100vh;
  }

  @media (max-width: $mobile) {
    min-height: 220px;
    height: 260px;
    padding-top: $header-height-mobile;
  }
}

.hero-media {
  position: absolute;
  top: $header-height-desktop; /* 从header下方开始 */
  left: 0;
  width: 100%;
  height: calc(100% - #{$header-height-desktop}); /* 减去header高度 */
  overflow: hidden;
  z-index: 1;

  @media (max-width: $mobile) {
    top: $header-height-mobile;
    height: 200px;
  }
}

.hero-video,
.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  top: $header-height-desktop; /* 从header下方开始 */
  left: 0;
  height: calc(100% - #{$header-height-desktop}); /* 减去header高度 */
  width: 100%;
  display: flex;
  align-items: center;
  z-index: 2;
  padding: 0 10%;
  
  /* 移除背景遮罩 */
  background-color: transparent;
  
  &.content-only {
    /* 如果你需要为文本添加一点点阴影效果以保证可读性 */
    text-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  }

  @media (max-width: $mobile) {
    top: $header-height-mobile;
    height: calc(100% - #{$header-height-mobile});
  }
}

.hero-content {
  max-width: 600px;
  position: relative;
  z-index: 2;

  /* 为文本内容添加阴影效果以保证在明亮背景上的可读性 */
  .hero-title, .hero-subtitle, .hero-desc {
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  }

  &.simple-content {
    text-align: left;
    max-width: 500px;

    .hero-title {
      font-size: 50px;
      margin-bottom: 15px;
    }

    .hero-subtitle {
      font-size: 22px;
      line-height: 1.4;
      opacity: 0.9;
    }
  }
}

.hero-subtitle {
  color: $light-text;
  font-weight: 500;
  margin-bottom: 15px;
  font-size: 18px;
}

.hero-title {
  font-size: 42px;
  font-weight: bold;
  margin-bottom: 20px;
  line-height: 1.2;
  color: $light-text;

  @media (max-width: $mobile) {
    font-size: 32px;
  }
}

.hero-desc {
  font-size: 18px;
  margin-bottom: 25px;
  opacity: 0.9;
  color: $light-text;
}

.btn-primary {
  background-color: $primary-color;
  color: $light-text;
  border: none;
  padding: 12px 30px;
  border-radius: 30px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  font-size: 14px;

  &:hover {
    background-color: darken($primary-color, 10%);
  }
}

.banner-indicators {
  position: absolute;
  bottom: 40px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 12px;
  z-index: 10;

  @media (max-width: $mobile) {
    bottom: 8px;
    gap: 4px;
  }
}

.banner-line {
  width: 30px;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.6);
  transition: all 0.3s;
  cursor: pointer;

  &.active {
    background-color: $primary-color;
    width: 40px;
  }
}

.banner-arrows {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
  z-index: 10;

  @media (max-width: $mobile) {
    padding: 0 4px;
    top: calc(50% + 35px);
  }
}

.banner-arrow {
  width: 40px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: $primary-color;
  }

  @media (max-width: $mobile) {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
}

.banner-content {
  position: absolute;
  top: 50%;
  left: 10%;
  transform: translateY(-50%);
  z-index: 2;
  max-width: 50%;

  @media (max-width: $mobile) {
    left: 0;
    max-width: 100%;
    padding: 0 12px;
    top: 60%;
    transform: translateY(-50%);
  }
}

.banner-text {
  color: $light-text;
  font-size: 42px;
  font-weight: bold;
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  margin: 0;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(5px);
  }

  @media (max-width: $mobile) {
    font-size: 18px;
    line-height: 1.3;
    word-break: break-all;
  }
}

.banner-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.15);
  z-index: 20;
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #fff;
    border-top: 4px solid #ed1c24;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
